/**
 * Narrative-trigger definitions (R11) — standing condition-watchers, the
 * reserved step-7 machinery of architecture.md §7.2/§7.3.
 *
 * @remarks
 * A trigger is the piece the scheduler deliberately is not: **"fire when
 * this becomes true, whenever that happens."** An event needs an arming
 * moment and a due occurrence whose failed condition check DROPS it forever
 * — Calder's countdown proved both edges of that (his 2026-08-22 §4.1
 * pump-plus-sensor workaround, and §4.3's "there is no genesis or room-entry
 * arming"). A trigger needs neither: it is evaluated at step 7 of every
 * executed turn, and fires **once per session, edge-triggered** — the first
 * turn its conditions all hold, its effects apply, and `firedTriggerIds`
 * retires it.
 *
 * **Edge-triggered and once by default**, with one narrow exception (D119).
 * The original reasoning stands for STATE watchers: a watcher that should act
 * repeatedly is a trigger arming a self-scheduling event — edge detector plus
 * metronome, each primitive whole (§2.1).
 *
 * What that reasoning did not cover is a DEVICE. Mike played a kettle whose
 * countdown was armed by an occurrence watcher, and found it could be armed
 * exactly once per save: the arm is not a beat that happens once, it is the
 * act of switching a thing on, which happens every time somebody does it.
 * `repeatable` exists for that case and is refused on state watchers, where
 * "every time" would mean "every turn the predicate holds" — a per-turn tick,
 * which is a different feature and should be asked for by name.
 *
 * The §7.3 matcher taxonomy (room entry, flag set, event fired, ending
 * reached) is expressible today as conditions — `room_equals`, `flag_equals`,
 * `event_fired`, `ending_selected` — and any future matcher syntax is sugar
 * that desugars into them.
 *
 * A trigger whose conditions hold at genesis fires on the session's first
 * executed turn — which is precisely the "start it at the beginning" a
 * future author's first instinct will reach for.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { conditionSchema } from './conditions.js';
import { effectSchema } from './effects.js';
import { triggerIdSchema } from './identifiers.js';
import { nonBlankStringSchema } from './primitives.js';

/**
 * The occurrence a trigger listens FOR (R12) — the §7.3 occurrence-shaped
 * matchers, as union growth on the R11 watcher.
 *
 * @remarks
 * A state watcher (no `on`) fires when its conditions BECOME true, at step 7.
 * An occurrence watcher fires when its occurrence HAPPENS — evaluated at the
 * occurrence's own site, because two of these never reach `executeTurn` at
 * all (a failed parse and a spoken reaction are decided in
 * `processPlayerInput`) and a rejection returns before step 7. Conditions
 * still gate the firing (`all_of`, evaluated at the occurrence), and firing
 * is once per session unless the trigger declares `repeatable` (D119).
 * Escalating VOICES are still authored as several triggers gated on
 * `occurrence_tally_at_least` at rising counts — she should say a different
 * thing the fifth time, not the same thing five times. `repeatable` is for
 * the other case: a mechanism that does the same thing every time, because
 * that is what mechanisms do.
 *
 * The optional discriminants narrow the listening: a bare `parse_failed`
 * hears every failure; `failureKind: 'gibberish…'` hears one kind. Values
 * are validated at load against the parser's and engine's own exported
 * lists — the schema stays mirror-not-import, like the vocabulary schemas.
 *
 * Occurrence triggers on NON-EXECUTED inputs (failed parses, rejections,
 * reactions) may only speak — `append_narrative` / `speak_voice_line` — a
 * refused input must never move the world (D27's "voice, not mechanics,"
 * generalized; validation enforces it).
 */
export const occurrenceMatcherSchema = z.discriminatedUnion('type', [
	z.strictObject({
		/** Narrow to one canonical intent token (load-validated against the compiled vocabulary). */
		intent: nonBlankStringSchema.optional(),
		type: z.literal('command_executed'),
	}),
	/*
	 * D235 (actors round B2): the player ANSWERED an actor's prompt — an
	 * executed turn like a command's, so a trigger listening here may move
	 * the world. `promptId` narrows to one question, `optionId` (with it) to
	 * one answer; bare, every answer to every prompt. The two-door lesson of
	 * 8c: an answer's consequence can live on the option, or on a watcher
	 * that heard it — EURYDICE commenting on what the player said to someone
	 * else is the watcher's shape.
	 */
	z
		.strictObject({
			/** Narrow to one answer of the named prompt (load-validated against its options). Requires `promptId`. */
			optionId: nonBlankStringSchema.optional(),
			/** Narrow to one prompt (load-validated against the pack's actors' prompts). */
			promptId: nonBlankStringSchema.optional(),
			type: z.literal('dialogue_answered'),
		})
		.refine((matcher) => matcher.optionId === undefined || matcher.promptId !== undefined, {
			message: 'optionId narrows an answer of ONE prompt — name the promptId it belongs to',
			path: ['optionId'],
		}),
	z.strictObject({
		/** Narrow to one rejection reason (load-validated against the engine's list). */
		reason: nonBlankStringSchema.optional(),
		type: z.literal('command_rejected'),
	}),
	z.strictObject({
		/** Narrow to one parse-failure kind (load-validated against the parser's list). */
		failureKind: nonBlankStringSchema.optional(),
		type: z.literal('parse_failed'),
	}),
	z.strictObject({
		/** Narrow to one D27 reaction key (load-validated against the pack's reactions). */
		reactionKey: nonBlankStringSchema.optional(),
		type: z.literal('reaction_spoken'),
	}),
]);

/** One occurrence matcher — see {@link occurrenceMatcherSchema}. */
export type OccurrenceMatcher = z.infer<typeof occurrenceMatcherSchema>;

/** One authored trigger — see the module remarks. */
export const triggerDefinitionSchema = z
	.strictObject({
		/**
		 * The gating predicate (`all_of` semantics). A STATE watcher must have
		 * at least one — a watcher with no predicate is watching nothing — and
		 * the refinement below enforces it. An occurrence watcher's WHEN is its
		 * occurrence, so bare conditions default to "always".
		 */
		conditions: z.array(conditionSchema).readonly().default([]),
		/** Applied in order on the turn the conditions first all hold. */
		effects: z.array(effectSchema).min(1).readonly(),
		/** R12: listen for an OCCURRENCE instead of a state edge — see {@link occurrenceMatcherSchema}. */
		on: occurrenceMatcherSchema.optional(),
		/*
		 * D119: fire EVERY time rather than once per session. The session guard
		 * in `narrative.firedTriggerIds` is right for a narrative beat — a line
		 * she delivers once is spoiled by delivering it twice — and wrong for a
		 * DEVICE, which is used repeatedly by definition. Found by Mike playing
		 * a kettle whose countdown could be armed exactly once per save, and
		 * whose spent trigger no change to the pack could revive.
		 *
		 * Termination is preserved by a narrower guard: a repeatable trigger is
		 * exempt from the SESSION list but still fires at most once per sweep,
		 * so a trigger whose own effects keep its conditions true cannot spin.
		 * The proof is the same shape it always was — the eligible set shrinks
		 * on every pass — only the ledger it shrinks against is per-turn.
		 *
		 * An occurrence watcher only: a repeatable STATE watcher would re-fire
		 * on every turn its predicate holds, which is a different feature (a
		 * per-turn tick) and should be asked for by that name.
		 */
		repeatable: z.literal(true).optional(),
		triggerId: triggerIdSchema,
	})
	.readonly()
	.refine((trigger) => trigger.on !== undefined || trigger.conditions.length > 0, {
		message: 'a state watcher (no `on`) needs at least one condition — a watcher with no predicate is watching nothing',
		path: ['conditions'],
	})
	.refine((trigger) => trigger.repeatable !== true || trigger.on !== undefined, {
		message:
			'`repeatable` needs an `on` — a repeatable state watcher would fire every turn its predicate holds, which is a per-turn tick and not this feature',
		path: ['repeatable'],
	});

/** One authored trigger — see {@link triggerDefinitionSchema}. */
export type TriggerDefinition = z.infer<typeof triggerDefinitionSchema>;
